import { faBell } from '@fortawesome/pro-regular-svg-icons/faBell';
import { faBell as faBellSolid } from '@fortawesome/pro-solid-svg-icons/faBell';
import { faTimes } from '@fortawesome/pro-solid-svg-icons/faTimes';
import {
  AUCTION_CATEGORIES,
  AUCTION_SORT_ORDER_OPTIONS,
  SORT_ORDER_OPTIONS,
} from '@propertypal/shared/src/constants/filterOptions';
import useInView from '@propertypal/shared/src/hooks/useInView';
import useLocalStorage from '@propertypal/shared/src/hooks/useLocalStorage';
import { UseSearchState } from '@propertypal/shared/src/hooks/useSearchState';
import { selectProperties } from '@propertypal/shared/src/reducers/properties';
import CoOwnershipLogo from '@propertypal/shared/src/resources/logos/co-ownership-logo.svg?url';
import { trackGaEvent } from '@propertypal/shared/src/services/analytics';
import getTotalProperties from '@propertypal/shared/src/utils/search/getTotalProperties';
import ButtonPillRow from '@propertypal/web-ui/src/buttons/ButtonPillRow';
import FontAwesomeIcon from '@propertypal/web-ui/src/icons/FontAwesomeIcon';
import SelectInput from '@propertypal/web-ui/src/inputs/SelectInput';
import ToggleSwitch from '@propertypal/web-ui/src/inputs/ToggleSwitch';
import { closeTooltip, openTooltip } from '@propertypal/web-ui/src/notifications/Tooltip';
import DidYouMean from '@propertypal/web-ui/src/search/DidYouMean';
import { Bold, Heading, LinkText, Text } from '@propertypal/web-ui/src/typography';
import React, { FunctionComponent, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useTheme } from 'styled-components';
import OpenShowPills from './OpenShowPills';
import {
  Title,
  CoOwnershipBanner,
  CoOwnershipHide,
  CoOwnershipBannerConent,
  SwitchRow,
  TopRow,
  MiddleRow,
  SwitchLabel,
  SwitchLabelMobile,
} from './SearchTitle.style';

interface Props {
  handleSortChange: (sort: string) => void;
  onAlternativeSearch: (term: string) => void;
  onSaveSearchClick: () => void;
  searchState: UseSearchState;
  onMap?: boolean;
  searchUrl?: string;
}

const ALERT_TEXT_MAP: { [key: string]: string } = {
  twiceDaily: 'Twice Daily',
  never: 'Never',
  instant: 'Instant',
  daily: 'Daily',
  weekly: 'Weekly',
};

const SearchTitle: FunctionComponent<Props> = (props) => {
  const theme = useTheme();
  const { appFilters, nlpFilters, isSavable, disableSaveSearch } = props.searchState;
  const filters = { ...appFilters, ...nlpFilters };
  const properties = useSelector(selectProperties);
  const savedSearch = properties.data?.savedSearch;
  const hasActiveAlert = !!savedSearch && savedSearch.emailFrequency !== 'never';
  const showTotalProperties = !['COUNTRY', 'REGION'].includes(properties.data?.locationDetail?.termType || 'COUNTRY');
  const isBeyondLastPage =
    properties.data && properties.data.totalPages > 0 && properties.data.page + 1 > properties.data.totalPages;
  const showNoResults = properties.data?.results && properties.data.results.length === 0 && !isBeyondLastPage;
  const [propertyType, formattedTotal] = getTotalProperties(
    filters.category,
    properties.data?.totalUnits,
    properties.data?.totalDevelopments,
  );
  const coOwnershipRef = useRef<HTMLDivElement>(null);
  const [hideCoOwnership, setHideCoOwnership] = useLocalStorage<boolean>('hideCoOwnership', false);

  useInView(coOwnershipRef, { threshold: 0.8 }, () => {
    trackGaEvent('coownership_banner_impression', {
      page: 'SRP',
      location: 'Search Results',
    });
  });

  const pillButtons = [
    {
      text: 'List',
      active: true,
    },
    {
      text: 'Map',
      href: `/map${props.searchUrl || ''}`,
      active: false,
    },
  ];

  if (props.onMap) {
    return null;
  }

  return (
    <div>
      <TopRow>
        {properties.data && !showNoResults && (
          <div style={{ flex: 1 }}>
            <Title id="search-title">
              {properties.data.description.stem}
              <Text as="span" mt={5} mb={10} style={{ display: 'block' }}>
                {properties.data.description.details}
              </Text>
            </Title>

            <Text>
              Page <Bold color={theme.primary}>{properties.data.page + 1}</Bold> of {properties.data.totalPages}
              {showTotalProperties && formattedTotal !== '0' && (
                <>
                  {' '}
                  (<Bold color={theme.primary}>{formattedTotal}</Bold> {`${propertyType})`}
                </>
              )}
            </Text>
          </div>
        )}

        <ButtonPillRow buttons={pillButtons} />
      </TopRow>

      <MiddleRow>
        <SwitchRow
          $active={hasActiveAlert}
          onMouseEnter={(e) => {
            if (disableSaveSearch) {
              openTooltip(
                'You must click the "Search" button in the search form to update your results before you can save this search.',
                e,
              );
            }
          }}
          onMouseLeave={closeTooltip}
        >
          <FontAwesomeIcon icon={hasActiveAlert ? faBellSolid : faBell} size={20} />
          <SwitchLabel>{hasActiveAlert ? ALERT_TEXT_MAP[savedSearch.emailFrequency] : 'Property alert'}</SwitchLabel>

          <SwitchLabelMobile>
            {hasActiveAlert ? ALERT_TEXT_MAP[savedSearch.emailFrequency] : 'Alerts'}
          </SwitchLabelMobile>

          <ToggleSwitch
            checked={hasActiveAlert}
            onValueChange={props.onSaveSearchClick}
            disabled={!isSavable || disableSaveSearch}
            testID="save-search-toggle"
          />

          {!!savedSearch && (
            <LinkText
              as="button"
              type="button"
              fontSize={13}
              onClick={props.onSaveSearchClick}
              style={{ background: 'none', border: 'none', padding: 0 }}
              data-testid="edit-frequency-link"
            >
              Edit frequency
            </LinkText>
          )}
        </SwitchRow>

        <SelectInput
          containerStyle={{ flex: 0 }}
          ariaLabel="Select a sort order"
          id="sortOrderSelect"
          label="Sort by"
          options={AUCTION_CATEGORIES.includes(filters.category) ? AUCTION_SORT_ORDER_OPTIONS : SORT_ORDER_OPTIONS}
          value={filters.sortOrder}
          onChange={props.handleSortChange}
          testID="searchSortOrder"
          alt
        />
      </MiddleRow>

      <OpenShowPills searchResult={properties.data} filters={filters} />

      {showNoResults && (
        <div>
          <Heading data-testid="noPropertiesHeading">No Properties Found</Heading>

          <Text mt={5}>
            Sorry. We couldn&apos;t find any results for the search &quot;{properties.data?.description.stem}&quot;
          </Text>
        </div>
      )}

      {properties.data?.coOwnershipEligible && !hideCoOwnership && (
        <CoOwnershipBanner data-testid="co-ownership-banner" ref={coOwnershipRef}>
          <CoOwnershipHide
            icon={faTimes}
            onClick={() => {
              trackGaEvent('coownership_banner_hide', {
                page: 'SRP',
                location: 'Search Results',
              });
              setHideCoOwnership(true);
            }}
            data-testid="close-co-ownership-banner"
          />
          <CoOwnershipBannerConent>
            <img src={CoOwnershipLogo.src} className="co-ownership-logo" alt="Co-Ownership" />
            <span>
              has helped over 35,000 people become homeowners. Find out if they could{' '}
              <a
                href="https://bit.ly/3SFUtCI"
                target="_blank"
                rel="noreferrer"
                onClick={() => {
                  trackGaEvent('coownership_banner_click', {
                    page: 'SRP',
                    location: 'Search Results',
                  });
                }}
                data-testid="coownership-link"
              >
                help you
              </a>{' '}
              buy the home you want.
            </span>
          </CoOwnershipBannerConent>
        </CoOwnershipBanner>
      )}

      {properties.data &&
        properties.data.results.length > 0 &&
        properties.data?.alternativeSearches &&
        Object.keys(properties.data?.alternativeSearches).length > 0 && (
          <DidYouMean searchResult={properties.data} onClick={props.onAlternativeSearch} />
        )}
    </div>
  );
};

export default SearchTitle;
